import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { Quote, QuoteOrderLine, QuoteOrderTranche } from '../../types';
import { Button } from '../Button';
import { OrderLinesPicker } from './OrderLinesPicker';
import {
  computeTrancheNet,
  formatEur,
  formatQty,
  initialOrderSelection,
  linesFromSelection,
  OrderSelection,
  parseOrderQty,
  quoteOrderSummary,
  isTrancheInOrderValue,
  statusAfterLines,
  validateOrderLines,
} from '../../services/quoteOrderTranches';
import { loadTrancheContext, previewTranche, TrancheContext } from '../../services/quoteOrderService';
import { isSupplementQuote } from '../../services/quoteSupplementLogic';
import { auftragswertCentsAusAngebot, auftragswertCentsAusEingabe, centsAlsEingabe } from '../../services/erpSteering';
import type { FlatInclusionDecision } from '../../services/flatRateLogic';
import { describeError } from '../../services/errorMessage';

/**
 * Dialog zu einem späteren Auftragsteil eines teilbeauftragten Angebots:
 *
 *  - `order`  — Rest (ganz oder teilweise) beauftragen: Mengen wählen, Vorschau
 *               der Änderung im Projekt-LV, Monatspauschale ja/nein für neue
 *               Positionen, neuer Auftragswert, ggf. neues Projektende.
 *  - `decline`— Rest entfällt: Mengen wählen, Begründung Pflicht.
 *  - `retry`  — ein erfasster, aber nicht eingetragener Teil wird ins Projekt
 *               eingetragen (Mengen fest, sonst wie `order`).
 *
 * Geschrieben wird nichts hier — onConfirm übergibt die Werte dem Editor.
 */
export interface TrancheDialogResult {
  lines: QuoteOrderLine[];
  orderDate: string;
  customerRef?: string;
  note?: string;
  flatDecisions: Partial<Record<string, FlatInclusionDecision>>;
  endDate?: string;
  orderValueCents?: number;
}

interface Props {
  quote: Quote;
  mode: 'order' | 'decline' | 'retry';
  tranche?: QuoteOrderTranche;
  busy?: boolean;
  error?: string;
  onCancel: () => void;
  onConfirm: (result: TrancheDialogResult) => void;
}

const inputClass =
  'w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';
const labelClass = 'text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1';

const todayIso = () => new Date().toISOString().slice(0, 10);

export const QuoteTrancheDialog: React.FC<Props> = ({ quote, mode, tranche, busy, error, onCancel, onConfirm }) => {
  const needsProject = mode !== 'decline';
  const [ctx, setCtx] = useState<TrancheContext | null>(null);
  const [ctxError, setCtxError] = useState('');
  const [selection, setSelection] = useState<OrderSelection>(() => initialOrderSelection(quote, true));
  const [orderDate, setOrderDate] = useState(todayIso);
  const [customerRef, setCustomerRef] = useState('');
  const [note, setNote] = useState('');
  const [endDate, setEndDate] = useState('');
  const [flatDecisions, setFlatDecisions] = useState<Partial<Record<string, FlatInclusionDecision>>>({});
  const [orderValueText, setOrderValueText] = useState('');
  const [orderValueTouched, setOrderValueTouched] = useState(false);

  useEffect(() => {
    if (!needsProject) return;
    let alive = true;
    loadTrancheContext(quote)
      .then((c) => { if (!alive) return; setCtx(c); setEndDate(c.endDate || ''); })
      .catch((e) => { if (alive) setCtxError(describeError(e)); });
    return () => { alive = false; };
    // Einmal je Angebot: die Vorschau rechnet danach lokal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote.id, needsProject]);

  const lines = useMemo(
    () => (mode === 'retry' ? tranche?.lines || [] : linesFromSelection(selection)),
    [mode, tranche, selection],
  );
  const errors = useMemo(() => (mode === 'retry' ? [] : validateOrderLines(quote, lines)), [mode, quote, lines]);
  const unreadable = mode !== 'retry'
    && Object.values(selection).some((v) => v.on && !((parseOrderQty(v.text) ?? 0) > 0));
  const net = useMemo(() => {
    if (mode === 'retry') return tranche?.netAmount ?? 0;
    return lines.length > 0 && errors.length === 0 ? computeTrancheNet(quote, lines) : 0;
  }, [mode, tranche, quote, lines, errors.length]);
  const closes = mode !== 'retry' && errors.length === 0 && lines.length > 0 && statusAfterLines(quote, lines) === 'accepted';
  // Erneut eintragen: der erfasste Teil geht mit — beim Nachtrag gilt seine
  // gespeicherte Zuordnung (quoteOrderService.supplementTranchePlan).
  const preview = useMemo(
    () => (ctx && needsProject ? previewTranche(ctx, quote, lines, mode === 'retry' ? tranche : undefined) : null),
    [ctx, needsProject, quote, lines, mode, tranche],
  );
  const pendingFlat = (preview?.flatDecisionIds || []).filter((id) => !flatDecisions[id]);
  // Paketposition ueber Menge 1: gesperrt (quoteOrderTranches.paketErhoehungGesperrt).
  // Der Dienst prueft dasselbe noch einmal vor dem Schreiben.
  const packageBlocks = preview?.plan.packageBlocks ?? [];

  // Auftragswert-Vorschlag: bisher fixiert + dieser Teil; ohne fixierten Wert
  // die Summe aller beauftragten Teile + dieser. Beim erneuten Eintragen steckt
  // der Teil womoeglich schon im Wert (Vermerk traegt seine Kennung) — dann
  // nicht ein zweites Mal addieren.
  //  Nachtrag ohne fixierten Wert: KEIN Vorschlag — die Summe der Teile ist
  //  nur der Nachtrag, als Auftragswert des ganzen Projekts fiele der
  //  Hauptauftrag heraus (wie suggestSupplementOrderValueCents).
  const suggestedCents = useMemo((): number | null => {
    if (!ctx) return 0;
    if (ctx.orderValueCents == null) {
      if (isSupplementQuote(quote)) return null;
      const others = quoteOrderSummary(quote).orderedNet - (mode === 'retry' ? net : 0);
      return auftragswertCentsAusAngebot(others + net);
    }
    const counted = mode === 'retry' && !!tranche && isTrancheInOrderValue(ctx.orderValueNote, tranche);
    return ctx.orderValueCents + (counted ? 0 : auftragswertCentsAusAngebot(net));
  }, [ctx, quote, net, mode, tranche]);
  useEffect(() => {
    if (!orderValueTouched && ctx) setOrderValueText(suggestedCents == null ? '' : centsAlsEingabe(suggestedCents));
  }, [suggestedCents, orderValueTouched, ctx]);
  const orderValueCents = auftragswertCentsAusEingabe(orderValueText);

  const expired = !!quote.validUntil && quote.validUntil.slice(0, 10) < todayIso();
  const title = mode === 'order' ? 'Rest beauftragen' : mode === 'decline' ? 'Rest entfällt' : `Teil ${tranche?.seq ?? ''} ins Projekt eintragen`;

  const canConfirm = !busy
    && errors.length === 0 && !unreadable && lines.length > 0
    && (!needsProject || (!!ctx && !ctx.blocker && pendingFlat.length === 0 && packageBlocks.length === 0))
    && (mode !== 'decline' || note.trim().length > 0);

  const handleConfirm = () => {
    onConfirm({
      lines,
      orderDate,
      customerRef: customerRef.trim() || undefined,
      note: note.trim() || undefined,
      flatDecisions,
      endDate: endDate && endDate !== (ctx?.endDate || '') ? endDate : undefined,
      orderValueCents: orderValueCents && orderValueCents > 0 ? orderValueCents : undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 animate-fade-in backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-800">{title}</h2>
            <p className="text-sm text-slate-500">
              {quote.quoteNumber}
              {ctx?.projectNumber ? ` · Projekt ${ctx.projectNumber}${ctx.projectName ? ` ${ctx.projectName}` : ''}` : ''}
            </p>
          </div>
          <button onClick={onCancel} disabled={busy} className="p-2 text-slate-400 hover:text-slate-600 rounded-xl disabled:opacity-50" title="Abbrechen">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {expired && mode === 'order' && (
            <div className="flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Das Angebot war gültig bis {new Date(quote.validUntil!).toLocaleDateString('de-DE')}. Der Rest wird zu den
                Angebotspreisen beauftragt.
              </span>
            </div>
          )}

          {needsProject && !ctx && !ctxError && (
            <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Projekt wird geladen…</p>
          )}
          {(ctxError || ctx?.blocker) && (
            <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{ctxError || ctx?.blocker}</span>
            </div>
          )}

          {mode === 'retry' ? (
            <div className="rounded-xl border border-slate-200 p-4 text-sm text-slate-700 space-y-1">
              {(tranche?.lines || []).map((l) => {
                const it = quote.items.find((i) => i.id === l.itemId);
                return (
                  <div key={l.itemId}>
                    <span className="font-mono text-xs text-slate-400 mr-2">{it?.posNumber || it?.lvPosition}</span>
                    {it?.title || l.itemId}: +{formatQty(l.quantity)} {it?.unit}
                  </div>
                );
              })}
            </div>
          ) : (
            <OrderLinesPicker quote={quote} selection={selection} onChange={setSelection} disabled={busy} />
          )}

          <p className="text-sm text-slate-600">
            {mode === 'decline' ? 'Entfällt' : 'Beauftragt'} <strong className="text-slate-800">{formatEur(net)}</strong>
            <span className="text-slate-400"> netto</span>
            {closes && <span className="text-slate-500"> — danach ist nichts mehr offen.</span>}
          </p>
          {lines.length > 0 && errors.length > 0 && (
            <p className="text-xs font-semibold text-red-600">{errors.join(' ')}</p>
          )}

          {/* Vorschau: was im Projekt-LV passiert. */}
          {preview && preview.plan.steps.length > 0 && (
            <div>
              <label className={labelClass}>Änderung im Projekt-LV</label>
              <div className="mt-1 border border-slate-200 rounded-xl divide-y divide-slate-100 text-sm">
                {preview.plan.steps.map((s) => {
                  // Neue Position: ihre id (beim Nachtrag selten ungleich itemId).
                  const newId = s.kind === 'append' ? s.serviceId ?? s.itemId : s.itemId;
                  return (
                  <div key={s.itemId} className="px-4 py-2">
                    <div className="text-slate-700">
                      <span className="font-mono text-xs text-slate-400 mr-2">{s.lvPosition}</span>
                      {s.title}
                      {': '}
                      {s.kind === 'append'
                        ? <span className="font-semibold text-blue-700">neu, {formatQty(s.to)} {s.unit}</span>
                        : <span>Vertrag {formatQty(s.from)} → <strong>{formatQty(s.to)}</strong> {s.unit}</span>}
                    </div>
                    {s.priceDiffers && (
                      <div className="text-[11px] font-semibold text-amber-700">
                        Preis im Projekt {formatEur(s.priceDiffers.project)} weicht vom Angebot {formatEur(s.priceDiffers.quote)} ab — die Menge wird trotzdem erhöht.
                      </div>
                    )}
                    {s.forcedAppendReason && (
                      <div className="text-[11px] font-semibold text-amber-700">{s.forcedAppendReason} — eigene Position.</div>
                    )}
                    {s.bestandteileHinweis && (
                      <div className="text-[11px] font-semibold text-amber-700">{s.bestandteileHinweis}</div>
                    )}
                    {s.packageBlock && (
                      <div className="mt-1 flex gap-1.5 text-xs font-semibold text-red-700">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>{s.packageBlock}</span>
                      </div>
                    )}
                    {preview.flatDecisionIds.includes(newId) && (
                      <div className="mt-1.5 flex items-center gap-2 text-xs">
                        <span className="font-semibold text-slate-600">In der Monatspauschale enthalten?</span>
                        {(['included', 'excluded'] as const).map((d) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => setFlatDecisions((prev) => ({ ...prev, [newId]: d }))}
                            className={`px-2.5 py-1 rounded-lg border font-bold ${flatDecisions[newId] === d ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'}`}
                          >
                            {d === 'included' ? 'ja' : 'nein'}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
              {pendingFlat.length > 0 && (
                <p className="text-xs font-semibold text-amber-700 mt-1 ml-1">
                  Für jede neue Position bitte entscheiden: in der Monatspauschale enthalten — ja oder nein.
                </p>
              )}
              {packageBlocks.length > 0 && (
                <div className="mt-2 flex gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    {packageBlocks.length === 1
                      ? 'Dieser Teil kann so nicht eingetragen werden: '
                      : `Dieser Teil kann so nicht eingetragen werden (${packageBlocks.length} Paketpositionen): `}
                    {packageBlocks[0].message}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{mode === 'decline' ? 'Absage am' : 'Beauftragt am'}</label>
              <input type="date" className={inputClass} value={orderDate} onChange={(e) => setOrderDate(e.target.value)} disabled={mode === 'retry'} />
            </div>
            {mode !== 'decline' && (
              <div>
                <label className={labelClass}>Bestellnummer Kunde</label>
                <input className={inputClass} value={mode === 'retry' ? tranche?.customerRef || '' : customerRef}
                       placeholder="optional" onChange={(e) => setCustomerRef(e.target.value)} disabled={mode === 'retry'} />
              </div>
            )}
          </div>

          {mode === 'decline' && (
            <div>
              <label className={labelClass}>Begründung *</label>
              <textarea className={`${inputClass} resize-none`} rows={2} value={note}
                        placeholder="z. B. Kunde vergibt die Bauphase anderweitig"
                        onChange={(e) => setNote(e.target.value)} />
            </div>
          )}

          {needsProject && ctx && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Auftragswert netto neu (EUR)</label>
                <input className={inputClass} inputMode="decimal" value={orderValueText} placeholder="0,00"
                       onChange={(e) => { setOrderValueTouched(true); setOrderValueText(e.target.value); }} />
                <p className="text-[11px] text-slate-500 mt-1 ml-1">
                  {ctx.orderValueCents != null
                    ? `Bisher ${formatEur(ctx.orderValueCents / 100)} + dieser Teil. Leer = unverändert.`
                    : isSupplementQuote(quote)
                      ? 'Bisher kein Auftragswert fixiert — der Nachtrag allein ist nicht der Auftragswert des Projekts. Leer = keiner.'
                      : 'Bisher kein Auftragswert fixiert — Vorschlag: alle beauftragten Teile. Leer = keiner.'}
                </p>
              </div>
              <div>
                <label className={labelClass}>Projektende</label>
                <input type="date" className={inputClass} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                <p className="text-[11px] text-slate-500 mt-1 ml-1">Nur ändern, wenn sich die Laufzeit verlängert.</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>Abbrechen</Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}
                  className={mode === 'decline' ? '!bg-red-600 hover:!bg-red-700 border-b-2 border-red-700' : undefined}>
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Wird gespeichert…</> : mode === 'decline' ? 'Rest entfällt' : mode === 'retry' ? 'Eintragen' : 'Beauftragen'}
          </Button>
        </div>
      </div>
    </div>
  );
};
